import React, { useEffect, useState } from 'react'
import {
  CheckCircleIcon,
  CalendarIcon,
  MapPinIcon,
  EyeIcon,
  Loader2Icon,
  FileTextIcon,
  BriefcaseIcon,
  AlertTriangleIcon,
  ShieldCheckIcon,
  CreditCardIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { getInvoiceDisplayStatus, getInvoiceStatusColor, getInvoiceStatusLabel } from '../lib/invoiceStatus'

interface PortalJob {
  id: string
  title: string
  job_number?: string
  status: string
  scheduled_date?: string
  actual_end_time?: string
  actual_amount?: number
  quoted_amount?: number
  notes?: string
  customer?: { company_name: string } | null
  property?: { address: string; city?: string; state?: string } | null
}

interface PortalContract {
  id: string
  title: string
  contract_number?: string
  status: string
  start_date?: string
  end_date?: string
  total_amount?: number
  contract_total_amount?: number
  billing_frequency?: string
  customer?: { company_name: string } | null
}

interface ComplianceRecord {
  id: string
  source: string
  source_uid: string
  title: string | null
  description: string | null
  status: string | null
  category: string | null
  record_date: string | null
  amount_due: number | null
  property_id: string
  propertyName?: string
  propertyAddress?: string
}

interface PortalInvoice {
  id: string
  invoice_number: string
  total: number
  amount_paid?: number
  balance_due?: number
  status: string
  invoice_date?: string
  due_date?: string
}

export function ClientPortal() {
  const { profile } = useAuth()
  const customerId = profile?.customer_id || null
  const [activeContracts, setActiveContracts] = useState<PortalContract[]>([])
  const [upcomingJobs, setUpcomingJobs] = useState<PortalJob[]>([])
  const [recentJobs, setRecentJobs] = useState<PortalJob[]>([])
  const [invoices, setInvoices] = useState<PortalInvoice[]>([])
  const [complianceRecords, setComplianceRecords] = useState<ComplianceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    fetchData()
  }, [customerId])

  const fetchData = async () => {
    if (!customerId) {
      setLoading(false)
      setError('This account is not linked to a customer record yet. Please contact RBC Facility Services.')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const today = new Date().toISOString().split('T')[0]
      const selectFields = '*, customer:customers(company_name), property:properties(address, city, state)'

      let contractsData: PortalContract[] = []
      try {
        const { data, error } = await supabase
          .from('contracts')
          .select('*, customer:customers(company_name)')
          .eq('customer_id', customerId)
          .in('status', ['active', 'signed', 'in_process'])
          .order('start_date', { ascending: false })
        if (!error) contractsData = data || []
      } catch {}

      let upcomingData: PortalJob[] = []
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(selectFields)
          .eq('customer_id', customerId)
          .in('status', ['scheduled', 'in_progress'])
          .gte('scheduled_date', today)
          .order('scheduled_date', { ascending: true })
          .limit(10)
        if (!error) upcomingData = data || []
      } catch {}

      // Only approved jobs that have been explicitly published are visible —
      // this keeps reports/photos hidden until a reviewer confirms they're
      // customer-ready (see ReviewDetails "Approve Job").
      let recentData: PortalJob[] = []
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(selectFields)
          .eq('customer_id', customerId)
          .eq('status', 'approved')
          .not('portal_published_at', 'is', null)
          .order('actual_end_time', { ascending: false })
          .limit(10)
        if (!error) recentData = data || []
      } catch {}

      let invoicesData: PortalInvoice[] = []
      try {
        const { data, error } = await supabase
          .from('invoices')
          .select('id, invoice_number, total, amount_paid, balance_due, status, invoice_date, due_date')
          .eq('customer_id', customerId)
          .neq('status', 'draft')
          .order('invoice_date', { ascending: false })
          .limit(25)
        if (!error) {
          invoicesData = (data || []).map((inv: any) => {
            const total = Number(inv.total || 0)
            const amountPaid = Number(inv.amount_paid ?? 0)
            const balanceDue = Number(inv.balance_due ?? (total - amountPaid))
            return {
              ...inv,
              total,
              amount_paid: amountPaid,
              balance_due: balanceDue,
              status: getInvoiceDisplayStatus({ status: inv.status, balance_due: balanceDue, due_date: inv.due_date }),
            }
          })
        }
      } catch {}

      // Fetch open city compliance records for this customer's properties
      let complianceData: ComplianceRecord[] = []
      try {
        const propIds = new Set<string>([
          ...contractsData.map((c: any) => c.property_id).filter(Boolean),
          ...upcomingData.map((j: any) => j.property_id).filter(Boolean),
          ...recentData.map((j: any) => j.property_id).filter(Boolean),
        ])
        if (propIds.size > 0) {
          const { data: propData } = await supabase
            .from('properties')
            .select('id, name, address')
            .in('id', Array.from(propIds))

          const propMap: Record<string, { name: string; address: string }> = {}
          ;(propData || []).forEach((p: any) => { propMap[p.id] = { name: p.name, address: p.address } })

          const openSources = ['dsny_oath', 'oath_ecb', 'dob_ecb', 'hpd_violation', 'hpd_complaint', 'complaint_311']
          const { data: crData } = await supabase
            .from('property_city_records')
            .select('id, source, source_uid, title, description, status, category, record_date, amount_due, property_id')
            .in('property_id', Array.from(propIds))
            .in('source', openSources)
            .eq('dismissed', false)
            .order('record_date', { ascending: false })
            .limit(50)

          complianceData = (crData || []).map((r: any) => ({
            ...r,
            propertyName:    propMap[r.property_id]?.name,
            propertyAddress: propMap[r.property_id]?.address,
          }))
        }
      } catch {}

      setActiveContracts(contractsData)
      setUpcomingJobs(upcomingData)
      setRecentJobs(recentData)
      setInvoices(invoicesData)
      setComplianceRecords(complianceData)
    } catch (err: any) {
      console.error('Error fetching client portal data:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const totalContractValue = activeContracts.reduce(
    (sum, c) => sum + Number(c.contract_total_amount || c.total_amount || 0),
    0
  )

  const tabs = [
    { id: 'overview', label: 'Overview', icon: EyeIcon },
    { id: 'contracts', label: 'Contracts', icon: FileTextIcon },
    { id: 'jobs', label: 'Jobs', icon: BriefcaseIcon },
    { id: 'invoices', label: 'Invoices', icon: CreditCardIcon },
  ]

  if (loading) {
    return (
      <div className="space-y-6 px-4 flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!customerId) {
    return (
      <div className="px-4 py-12 max-w-lg mx-auto text-center">
        <AlertTriangleIcon className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
        <h2 className="text-xl font-semibold text-gray-900">Account not linked</h2>
        <p className="mt-2 text-gray-600">{error || 'This account is not linked to a customer record yet. Please contact RBC Facility Services.'}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">RBC Field Management</h1>
              <p className="text-gray-600">Client Portal</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{activeContracts.length} active contract{activeContracts.length !== 1 ? 's' : ''}</p>
              <p className="text-sm font-medium text-gray-900">
                ${totalContractValue.toLocaleString(undefined, { minimumFractionDigits: 2 })} total value
              </p>
            </div>
      </div>

      {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <FileTextIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Contracts</p>
                <p className="text-xl font-bold text-gray-900">{activeContracts.length}</p>
              </div>
            </div>
          </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-50 text-yellow-600">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Upcoming Jobs</p>
                <p className="text-xl font-bold text-gray-900">{upcomingJobs.length}</p>
              </div>
            </div>
          </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50 text-green-600">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Recently Completed</p>
                <p className="text-xl font-bold text-gray-900">{recentJobs.length}</p>
              </div>
            </div>
          </div>
        </div>

      {/* Tabs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Upcoming Jobs */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Jobs</h3>
                  {upcomingJobs.length === 0 ? (
                    <p className="text-gray-500">No upcoming jobs scheduled.</p>
                  ) : (
                    <div className="space-y-3">
                      {upcomingJobs.slice(0, 5).map((job) => (
                        <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{job.title}</p>
                            <div className="flex items-center gap-3 mt-1">
                              {job.scheduled_date && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <CalendarIcon className="w-3 h-3" />
                                  {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()}
                                </span>
                              )}
                              {job.property && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <MapPinIcon className="w-3 h-3" />
                                  {job.property.address}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            job.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Completed */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recently Completed</h3>
                  {recentJobs.length === 0 ? (
                    <p className="text-gray-500">No recently completed jobs.</p>
                  ) : (
                    <div className="space-y-3">
                      {recentJobs.slice(0, 5).map((job) => (
                        <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{job.title}</p>
                            <div className="flex items-center gap-3 mt-1">
                              {job.actual_end_time && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <CheckCircleIcon className="w-3 h-3" />
                                  {new Date(job.actual_end_time).toLocaleDateString()}
                                </span>
                              )}
                              {job.property && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <MapPinIcon className="w-3 h-3" />
                                  {job.property.address}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            completed
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'contracts' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Contracts</h3>
                {activeContracts.length === 0 ? (
                  <p className="text-gray-500">No active contracts found.</p>
                ) : (
                  <div className="space-y-4">
                    {activeContracts.map((contract) => (
                      <div key={contract.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{contract.title}</p>
                            {contract.contract_number && (
                              <p className="text-xs text-gray-500">#{contract.contract_number}</p>
                            )}
                          </div>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 capitalize">
                            {contract.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">Customer</p>
                            <p className="font-medium">{contract.customer?.company_name || '—'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Value</p>
                            <p className="font-medium">
                              ${Number(contract.contract_total_amount || contract.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Start</p>
                            <p className="font-medium">
                              {contract.start_date
                                ? new Date(contract.start_date + 'T00:00:00').toLocaleDateString()
                                : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">End</p>
                            <p className="font-medium">
                              {contract.end_date
                                ? new Date(contract.end_date + 'T00:00:00').toLocaleDateString()
                                : '—'}
                            </p>
                          </div>
                        </div>
                        {contract.billing_frequency && (
                          <p className="text-xs text-gray-500 mt-2 capitalize">
                            Billing: {contract.billing_frequency}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'jobs' && (
              <div className="space-y-6">
                {/* Upcoming */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Upcoming & In Progress</h3>
                  {upcomingJobs.length === 0 ? (
                    <p className="text-gray-500">No upcoming jobs.</p>
                  ) : (
                    <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {upcomingJobs.map((job) => (
                            <tr key={job.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{job.title}</div>
                                {job.job_number && <div className="text-xs text-gray-500">#{job.job_number}</div>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {job.property?.address || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {job.scheduled_date
                                  ? new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()
                                  : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  job.status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {job.status.replace('_', ' ')}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Recent Completed */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Recently Completed</h3>
                  {recentJobs.length === 0 ? (
                    <p className="text-gray-500">No completed jobs.</p>
                  ) : (
                    <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {recentJobs.map((job) => (
                            <tr key={job.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{job.title}</div>
                                {job.job_number && <div className="text-xs text-gray-500">#{job.job_number}</div>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {job.property?.address || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {job.actual_end_time
                                  ? new Date(job.actual_end_time).toLocaleDateString()
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                ${Number(job.actual_amount || job.quoted_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'invoices' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoices</h3>
                {invoices.length === 0 ? (
                  <p className="text-gray-500">No invoices found.</p>
                ) : (
                  <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance Due</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              ${Number(invoice.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={Number(invoice.balance_due || 0) > 0 ? 'text-red-700 font-semibold' : 'text-green-700'}>
                                ${Number(invoice.balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getInvoiceStatusColor(invoice.status)}`}>
                                {getInvoiceStatusLabel(invoice.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Building Compliance Panel */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <AlertTriangleIcon className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Building Compliance</h3>
          <span className="text-sm text-gray-500 ml-auto">NYC city records for your properties</span>
        </div>
        {complianceRecords.length === 0 ? (
          <div className="flex items-center gap-3 px-6 py-8 text-gray-500">
            <ShieldCheckIcon className="w-6 h-6 text-green-500 shrink-0" />
            <div>
              <p className="font-medium text-gray-800">No open compliance records found.</p>
              <p className="text-sm">We'll show any city violations, complaints, or tickets here as they are filed.</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {complianceRecords.map((rec) => {
              const isEnforcement = rec.source === 'dsny_oath' || rec.source === 'oath_ecb' || rec.source === 'dob_ecb'
              const sourceLabel: Record<string, string> = {
                dsny_oath:     'DSNY Ticket',
                oath_ecb:      'OATH/ECB Ticket',
                dob_ecb:       'DOB/ECB Violation',
                hpd_violation: 'HPD Violation',
                hpd_complaint: 'HPD Complaint',
                complaint_311: '311 Complaint',
              }
              return (
                <div key={rec.id} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isEnforcement ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {sourceLabel[rec.source] || rec.source}
                        </span>
                        {rec.status && (
                          <span className="text-xs text-gray-500">{rec.status}</span>
                        )}
                        {rec.amount_due != null && rec.amount_due > 0 && (
                          <span className="text-sm font-bold text-red-700">
                            ${rec.amount_due.toFixed(2)} due
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {rec.title || rec.source_uid}
                      </p>
                      {rec.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{rec.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {rec.propertyAddress || rec.propertyName}
                        {rec.record_date && (
                          <span className="ml-2">
                            · {new Date(rec.record_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Customer Service</p>
              <p className="font-medium">(555) 123-4567</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Email Support</p>
              <p className="font-medium">support@rbcfm.com</p>
            </div>
          </div>
        </div>
    </div>
  )
}
